// 登录页 — 根据运行时 auth_mode 切换:
//   - local:      本地账号表单 + (可选)OAuth 圆形按钮
//                 点击「登录」先校验用户名/密码,通过后弹出 TeachingPopover 展示
//                 滑块验证码,完成拼图后点「确认登录」真正提交。
//   - oauth_only: 禁用本地表单,只显示 OAuth provider 列表 + 顶部 MessageBar
//                 没有可用 provider 时显示「请联系管理员」占位
// UI 是表现层,真正的护栏在 crates/server/src/routes/auth.rs(后端会独立返回 403)。
import { useEffect, useState } from 'react';
import {
  makeStyles,
  tokens,
  Button,
  Input,
  Label,
  Card,
  Spinner,
  Title3,
  Body1,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import { Scale } from '@fluentui/react-motion-components-preview';
import {
  TeachingPopover,
  TeachingPopoverTrigger,
  TeachingPopoverSurface,
  TeachingPopoverBody,
  TeachingPopoverTitle,
} from '@fluentui/react-teaching-popover';
import {
  PersonRegular,
  LockClosedRegular,
  ShieldCheckmarkRegular,
} from '@fluentui/react-icons';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import { DEFAULT_REDIRECT_AFTER_LOGIN } from '@/routes';
import { explainError } from '@/api/client';
import { authApi, type CaptchaChallenge } from '@/api/auth';
import { oauthApi, type OAuthProviderPublic } from '@/api/oauth';
import { SliderCaptcha } from '@/components/SliderCaptcha';
import { HelpPopover } from '@/components/HelpPopover';
import { toast } from '@/lib/toast';
import { getAuthMode } from '@/lib/authMode';

const useStyles = makeStyles({
  fullPage: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: `${tokens.spacingVerticalXXL} ${tokens.spacingHorizontalXL}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  // Fluent UI Card 内置 width:fit-content 会覆盖 CSS 类的 width,只能用 inline style 强制
  card: {
    maxWidth: '100%',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadow16,
  },
  cardBody: {
    padding: '28px',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  title: {
    textAlign: 'center',
    fontWeight: 600,
  },
  // 窄屏:logo + 标题纵向居中;桌面 ≥720px:横向排列
  brand: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    marginBottom: tokens.spacingVerticalM,
    '@media (min-width: 720px)': {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: tokens.spacingHorizontalM,
    },
  },
  logo: {
    width: '56px',
    height: '56px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: tokens.spacingVerticalM,
    gap: tokens.spacingVerticalXS,
  },
  fieldError: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase300,
  },
  submitButton: {
    width: '100%',
    marginTop: tokens.spacingVerticalXS,
  },
  // TeachingPopover 内容区:验证码 + 操作按钮
  popoverSurface: {
    width: '300px',
    maxWidth: '90vw',
  },
  popoverActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalS,
  },
  // OAuth 圆形按钮(紧凑,居中,减少垂直空间占用)
  ssoRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalS,
  },
  ssoCircle: {
    width: '52px',
    height: '52px',
    minWidth: '52px',
    padding: 0,
    flexShrink: 0,
  },
  ssoCircleIcon: {
    width: '24px',
    height: '24px',
    borderRadius: '4px',
    objectFit: 'contain',
  },
  ssoCircleLabel: {
    fontWeight: 600,
  },
  // oauth_only 模式:provider 列表
  oauthList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    marginTop: tokens.spacingVerticalM,
  },
  oauthItem: {
    width: '100%',
    justifyContent: 'flex-start',
    paddingLeft: tokens.spacingHorizontalM,
  },
  oauthItemIcon: {
    width: '20px',
    height: '20px',
    borderRadius: '4px',
    objectFit: 'contain',
    marginRight: tokens.spacingHorizontalS,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: tokens.spacingVerticalS,
    padding: `${tokens.spacingVerticalXL} 0`,
  },
});

export function LoginPage() {
  const styles = useStyles();
  const { login, isAuthenticated, loading } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  // ⚠️ 必须在任何提前 return 之前拿到 mode,因为 Hook 规则。
  const authMode = getAuthMode();
  const isOauthOnly = authMode === 'oauth_only';

  console.log('[gitman-dbg] <LoginPage/> render, authMode=', authMode);
  useEffect(() => {
    console.log('[gitman-dbg] <LoginPage/> mounted, isOauthOnly=', isOauthOnly, 'isAuthenticated=', isAuthenticated());
  }, [isOauthOnly, isAuthenticated]);

  const [providers, setProviders] = useState<OAuthProviderPublic[]>([]);

  // 本地登录表单(仅 local 模式用到)
  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{
    usernameOrEmail?: string;
    password?: string;
    captcha?: string;
  }>({});

  // 滑块验证码状态
  const [captcha, setCaptcha] = useState<CaptchaChallenge | null>(null);
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [captchaX, setCaptchaX] = useState(0);

  // TeachingPopover 受控开关:点击「登录」校验通过后打开
  const [captchaOpen, setCaptchaOpen] = useState(false);

  // 登录卡片入场动画:挂载后从 false→true 触发 Scale 缩放进入
  const [cardVisible, setCardVisible] = useState(false);
  useEffect(() => {
    setCardVisible(true);
  }, []);

  // 注意:所有 Hook 必须在任何提前 return 之前调用
  useEffect(() => {
    (async () => {
      try {
        const list = await oauthApi.listProviders();
        setProviders(list);
      } catch {
        // OAuth 不可用时静默
      }
    })();
  }, []);

  // 验证码按需加载:popover 打开时才拉取,不再页面加载时自动请求
  async function refreshCaptcha() {
    setCaptchaLoading(true);
    try {
      const challenge = await authApi.captcha();
      setCaptcha(challenge);
      setCaptchaX(0);
      setFieldErrors((prev) => ({ ...prev, captcha: undefined }));
    } catch (err) {
      toast.error(`获取验证码失败: ${explainError(err)}`);
    } finally {
      setCaptchaLoading(false);
    }
  }

  if (isAuthenticated()) {
    const from =
      (location.state as { from?: string } | null)?.from ?? DEFAULT_REDIRECT_AFTER_LOGIN;
    return <Navigate to={from} replace />;
  }

  // 仅校验用户名 + 密码(captcha 在 popover 里校验)
  function validateCredentials() {
    const errs: { usernameOrEmail?: string; password?: string } = {};
    if (!usernameOrEmail.trim()) errs.usernameOrEmail = '请输入用户名或邮箱';
    if (!password) errs.password = '请输入密码';
    setFieldErrors((prev) => ({ ...prev, ...errs }));
    return !errs.usernameOrEmail && !errs.password;
  }

  // 尝试打开验证码 popover:先校验凭据,通过才打开并加载验证码
  function tryOpenCaptcha() {
    if (validateCredentials()) {
      setCaptchaOpen(true);
      if (!captcha) refreshCaptcha();
    }
  }

  // TeachingPopover onOpenChange:打开时走凭据校验,关闭时直接关
  function handleCaptchaOpenChange(_: unknown, data: { open: boolean }) {
    if (data.open) {
      tryOpenCaptcha();
    } else {
      setCaptchaOpen(false);
    }
  }

  // Enter 键提交:校验通过后打开验证码 popover
  function handleEnterKey(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      tryOpenCaptcha();
    }
  }

  // 真正执行登录(popover 里「确认登录」按钮调用)
  async function doLogin() {
    if (!captcha) {
      toast.error('请先完成滑块验证');
      return;
    }
    try {
      await login(usernameOrEmail.trim(), password, captcha.token, captchaX);
      toast.success('登录成功');
      const from =
        (location.state as { from?: string } | null)?.from ?? DEFAULT_REDIRECT_AFTER_LOGIN;
      navigate(from, { replace: true });
    } catch (err) {
      const msg = explainError(err);
      console.error(msg);
      toast.error(msg);
      await refreshCaptcha();
    }
  }

  // OAuth2 入口:标准授权码 + PKCE,浏览器顶层跳转到 IdP 登录页
  function handleSsoClick(p: OAuthProviderPublic) {
    oauthApi.redirectToProviderLogin(p.name);
  }

  return (
    <div className={styles.fullPage}>
      <Scale visible={cardVisible} duration={300}>
      <Card className={styles.card} style={{ width: '380px' }}>
        <div className={styles.cardBody}>
          <div className={styles.brand}>
            <img src="/icons/logo-64.png" alt="Ming-GIT" className={styles.logo} />
            <Title3 as="h1" className={styles.title}>
              Ming-GIT
            </Title3>
          </div>

          {/* ── oauth_only:顶部说明 + OAuth provider 列表(无 provider 时占位)── */}
          {isOauthOnly ? (
            <>
              <MessageBar intent="info" icon={<ShieldCheckmarkRegular />}>
                <MessageBarBody>
                  <MessageBarTitle>当前仅支持 OAuth 2.0 登录</MessageBarTitle>
                  请使用下方提供的身份提供方登录。本地账号登录/注册已在系统设置中关闭。
                </MessageBarBody>
              </MessageBar>

              {providers.length === 0 ? (
                <div className={styles.emptyState}>
                  <ShieldCheckmarkRegular
                    fontSize={32}
                    style={{ color: tokens.colorNeutralForeground3 }}
                  />
                  <Body1>
                    暂未配置任何可用的登录方式。<br />
                    请联系管理员启用 OAuth 2.0 身份提供方。
                  </Body1>
                </div>
              ) : (
                <div className={styles.oauthList}>
                  {providers.map((p) => (
                    <Button
                      key={p.name}
                      appearance="outline"
                      size="large"
                      className={styles.oauthItem}
                      onClick={() => handleSsoClick(p)}
                    >
                      {p.icon ? (
                        <img src={p.icon} alt="" className={styles.oauthItemIcon} />
                      ) : (
                        <span
                          className={styles.oauthItemIcon}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: tokens.colorBrandBackground,
                            color: tokens.colorNeutralForegroundOnBrand,
                            fontWeight: 600,
                          }}
                        >
                          {p.name.charAt(0).toUpperCase()}
                        </span>
                      )}
                      {p.display_name || `使用 ${p.name} 登录`}
                    </Button>
                  ))}
                </div>
              )}
            </>
          ) : (
            /* ── local:本地账号登录表单 + (可选)OAuth 圆形按钮 ── */
            <>
              <form onSubmit={(e) => e.preventDefault()} autoComplete="off">
                {/* 用户名 */}
                <div className={styles.field}>
                  <Label htmlFor="login-username" required>
                    用户名 / 邮箱 <HelpPopover content="可用注册时的用户名,或绑定的邮箱登录,两者等效。" />
                  </Label>
                  <Input
                    id="login-username"
                    value={usernameOrEmail}
                    onChange={(_, d) => setUsernameOrEmail(d.value)}
                    onKeyDown={handleEnterKey}
                    contentBefore={<PersonRegular />}
                    placeholder="alice 或 alice@example.com"
                    autoFocus
                  />
                  {fieldErrors.usernameOrEmail && (
                    <span className={styles.fieldError}>{fieldErrors.usernameOrEmail}</span>
                  )}
                </div>

                {/* 密码 */}
                <div className={styles.field}>
                  <Label htmlFor="login-password" required>
                    密码 <HelpPopover content="本地账号的登录密码。使用 OAuth / SSO 登录的用户请点击下方的第三方登录按钮。" />
                  </Label>
                  <Input
                    id="login-password"
                    type="password"
                    value={password}
                    onChange={(_, d) => setPassword(d.value)}
                    onKeyDown={handleEnterKey}
                    contentBefore={<LockClosedRegular />}
                    placeholder="••••••"
                  />
                  {fieldErrors.password && (
                    <span className={styles.fieldError}>{fieldErrors.password}</span>
                  )}
                </div>

                {/* 登录按钮 = TeachingPopover trigger:校验通过后弹出验证码 */}
                <TeachingPopover
                  open={captchaOpen}
                  onOpenChange={handleCaptchaOpenChange}
                  positioning="below"
                >
                  <TeachingPopoverTrigger>
                    <Button
                      type="button"
                      appearance="primary"
                      icon={loading ? <Spinner size="tiny" appearance="inverted" /> : undefined}
                      className={styles.submitButton}
                    >
                      登录
                    </Button>
                  </TeachingPopoverTrigger>

                  <TeachingPopoverSurface className={styles.popoverSurface}>
                    <TeachingPopoverBody>
                      <TeachingPopoverTitle>滑块验证</TeachingPopoverTitle>
                      <SliderCaptcha
                        challenge={captcha}
                        onChange={setCaptchaX}
                        onRefresh={refreshCaptcha}
                        loading={captchaLoading}
                      />
                      {fieldErrors.captcha && (
                        <span className={styles.fieldError}>{fieldErrors.captcha}</span>
                      )}
                    </TeachingPopoverBody>
                    <div className={styles.popoverActions}>
                      <Button
                        appearance="subtle"
                        onClick={() => setCaptchaOpen(false)}
                        disabled={loading}
                      >
                        取消
                      </Button>
                      <Button
                        appearance="primary"
                        onClick={doLogin}
                        disabled={loading || !captcha}
                        icon={loading ? <Spinner size="tiny" appearance="inverted" /> : undefined}
                      >
                        确认登录
                      </Button>
                    </div>
                  </TeachingPopoverSurface>
                </TeachingPopover>
              </form>

              {providers.length > 0 && (
                <div className={styles.ssoRow}>
                  {providers.map((p) => (
                    <Button
                      key={p.name}
                      shape="circular"
                      appearance="outline"
                      className={styles.ssoCircle}
                      title={p.display_name || p.name}
                      aria-label={p.display_name || p.name}
                      onClick={() => handleSsoClick(p)}
                    >
                      {p.icon ? (
                        <img src={p.icon} alt="" className={styles.ssoCircleIcon} />
                      ) : (
                        <span className={styles.ssoCircleLabel}>
                          {p.name.charAt(0).toUpperCase() + p.name.slice(1)}
                        </span>
                      )}
                    </Button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </Card>
      </Scale>
    </div>
  );
}
